//@flow
import React, { useState } from 'react'

import FileManager from 'components/common/fileManagerTable/FileManager'
import { Button, Error, Flex, FileManagerTable, Pill, Icon, Audio } from 'components/common'
import { useIpbxApi } from '~/core/utils/hooks'
import { useI18n } from '~/core/i18n'
import { useSoundPlay } from '~/core/hooks'
import { useSoundsSelectModalStyles } from './styles'
import Label from '../../../label/label'

type SoundsSelectModalProps = {
  value?: string[],
  isMulti?: boolean,
  onChange: (value: string[]) => void
}

const SoundsSelectModal = ({ value, isMulti, onChange }: SoundsSelectModalProps) => {
  const [msg] = useI18n()
  const [path, setPath] = useState([])
  const [checkedRows, setCheckedRows] = useState(value || [])
  const classes = useSoundsSelectModalStyles()

  const {
    models: { audio: audioBlob, loading: audioLoading },
    operations: { handleSoundPlay }
  } = useSoundPlay({
    skipAutoPlay: true
  })

  const [{ data: soundsData, error: soundsError, loading: soundsLoading }] = useIpbxApi(
    {
      url: '/media/sounds'
    },
    []
  )

  const handleRemoveSelectedSound = (id: string) => {
    setCheckedRows(checkedRows.filter(soundId => soundId !== id))
  }

  const handleRowCheck = (id: string) => {
    if (checkedRows.includes(id)) {
      return setCheckedRows(checkedRows.filter(rowId => rowId !== id))
    }

    if (isMulti) {
      setCheckedRows([...checkedRows, id])
    } else {
      setCheckedRows([id])
    }
  }

  return (
    <Flex column id="sounds-select-modal">
      {soundsError && <Error reason={soundsError} />}
      <Flex alignCenter className={classes.selectedSoundsWrapper}>
        <span>
          <strong>{msg('pbx.media.sounds.content.selectedFiles')}</strong>
        </span>
        {checkedRows.map(id => (
          <Flex key={id} className={classes.selectedSoundBadge} id={`selected-${id}`}>
            <span className="selected-filename">{id}</span>
            <i className="icon" id={`remove-${id}`} onClick={() => handleRemoveSelectedSound(id)}>
              close
            </i>
          </Flex>
        ))}
      </Flex>
      <FileManagerTable
        checkedRows={checkedRows}
        customColumns={[
          {
            id: 'type',
            accessor: row => ({
              type: row.type,
              isDirectory: !!row.isDirectory,
              isAscendSelector: !!row.isAscendSelector
            }),
            minWidth: 200,
            Header: msg('pbx.media.sounds.content.type'),
            Cell: ({ original: { isDirectory, isAscendSelector, type } }: any) =>
              isDirectory || isAscendSelector ? (
                '-'
              ) : (
                <Pill color={type === 'system' ? 'gray' : 'orange'} size="small">
                  {type === 'system'
                    ? msg('pbx.media.sounds.content.systemSound')
                    : msg('pbx.media.sounds.content.customSound')}
                </Pill>
              ),
            sortMethod: FileManager.sortCustomColumn('type')
          },
          {
            id: 'play',
            sortable: false,
            alignRight: true,
            minWidth: 80,
            Header: 'Přehrát',
            Cell: ({ original }: any) => {
              if (original.isDirectory || original.isAscendSelector) {
                return '-'
              }

              return (
                <Flex justifyEnd>
                  <Icon
                    icon="volume_up"
                    isDisabled={audioLoading}
                    rounded
                    title={msg('pbx.media.sounds.content.playSound')}
                    onClick={() => handleSoundPlay({ filename: original.path, type: original.type })}
                  />
                </Flex>
              )
            }
          }
        ]}
        data={soundsData || []}
        loading={soundsLoading}
        path={path}
        style={{
          maxHeight: 300,
          minWidth: 500
        }}
        onPathChange={newPath => setPath(newPath)}
        onRowCheck={handleRowCheck}
      />

      <Flex alignFlexStart column margin={2}>
        <Label>
          {audioLoading ? msg('pbx.media.sounds.content.playerLoading') : msg('pbx.media.sounds.content.player')}
        </Label>
        <Audio autoPlay src={audioBlob} type="blob" />
      </Flex>

      <Flex
        justifyCenter
        style={{
          marginTop: '1rem'
        }}
      >
        <Button onClick={() => onChange(checkedRows)}>{msg('common.button.accept')}</Button>
      </Flex>
    </Flex>
  )
}

export default SoundsSelectModal
